import React, { useMemo, useState } from 'react';
import { Grid, Button, TextField, Autocomplete } from '@mui/material';
import {
	JobCode,
	SchedulerPosition,
	updatePosition,
} from '../../../../core/API/scheduler';
import { getPositionJobCodeId } from '../../../../module/schedule/utils/scheduleDepartmentUtils';
import styles from '../SchedulerSetting.module.scss';

interface EditPositionProps {
	position: SchedulerPosition;
	handleBackClick?: () => void;
	onPositionUpdated?: () => void;
	jobCodes: JobCode[];
	existingPositions: SchedulerPosition[];
}

const EditPosition: React.FC<EditPositionProps> = ({
	position,
	handleBackClick,
	onPositionUpdated,
	jobCodes,
	existingPositions,
}) => {
	const initialJobCodeId = getPositionJobCodeId(position);
	const [name, setName] = useState(position.name || '');
	const [selectedJobCode, setSelectedJobCode] = useState<JobCode | null>(
		jobCodes.find((jc) => jc.id === initialJobCodeId) || null
	);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const sortedJobCodes = useMemo(
		() =>
			[...jobCodes].sort((a, b) => {
				const aNum = a.jobNumber ?? 999;
				const bNum = b.jobNumber ?? 999;
				return aNum - bNum;
			}),
		[jobCodes]
	);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!name.trim()) {
			setError('Position name is required');
			return;
		}

		const duplicate = existingPositions.some(
			(p) => p.name.toLowerCase() === name.trim().toLowerCase() && p.id !== position.id
		);
		if (duplicate) {
			setError('Position name must be unique');
			return;
		}

		if (!selectedJobCode) {
			setError('Job code is required');
			return;
		}

		try {
			setIsSubmitting(true);
			await updatePosition(position.id, {
				name: name.trim(),
				jobCode: selectedJobCode['@id'],
			});
			onPositionUpdated?.();
			setTimeout(() => {
				handleBackClick?.();
			}, 500);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.['hydra:description'] ||
				err?.response?.data?.message ||
				(err instanceof Error ? err.message : 'Failed to update position');
			setError(errorMessage);
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.editJobCode}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={handleBackClick} variant="contained">
						Back
					</Button>
					<Button
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isSubmitting}
						variant="contained"
					>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Edit Position</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<TextField
					label="Position Name"
					name="name"
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="e.g. Station 17"
					className={styles.input}
					margin="normal"
					fullWidth
					required
				/>

				<Autocomplete
					options={sortedJobCodes}
					getOptionLabel={(option) =>
						option.jobNumber
							? `${String(option.jobNumber).padStart(2, '0')} - ${option.label}`
							: option.label
					}
					value={selectedJobCode}
					onChange={(_, value) => setSelectedJobCode(value)}
					isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Job Code"
							margin="normal"
							className={styles.input}
							required
						/>
					)}
				/>
			</form>
		</div>
	);
};

export default EditPosition;
